// The class represents a point on rope, containing its physics properties
export class VNode {
    public x: number = 0;
    public y: number = 0;
    public oldX: number = 0;
    public oldY: number = 0;
    public angle: number = 0;
    public gravity: number = 1;
    public mass: number = 1;
    public fixed: boolean = false;

    // Apply gravity if the node is not fixed. Rope has 2 fixed side nodes
    public integrate(): void {
        if (this.fixed) return;

        const x = this.x;
        const y = this.y;
        this.x += this.x - this.oldX;
        this.y += this.y - this.oldY + this.gravity;
        this.oldX = x;
        this.oldY = y;
    }

    public set(x: number, y: number): void {
        this.x = x;
        this.y = y;
        this.oldX = x;
        this.oldY = y;
    }
}

// This class applies physics iterations on each neighbor nodes pair
export class Constraint {
    public n0: VNode;
    public n1: VNode;
    private readonly dist: number;
    public stiffness: number; // works in range around 1...2
    private firstRun: boolean;

    constructor(n0: VNode, n1: VNode, stiffness: number) {
        this.n0 = n0;
        this.n1 = n1;
        this.dist = Math.hypot(n0.x - n1.x, n0.y - n1.y);
        this.stiffness = stiffness || 0.5;
        this.firstRun = true;
    }

    // Try to keep each node on initial distance to others
    public solve(): void {
        let dx = this.n0.x - this.n1.x;
        let dy = this.n0.y - this.n1.y;

        this.n1.angle = Math.atan2(dy, dx);

        const currentDist = Math.hypot(this.n0.x - this.n1.x, this.n0.y - this.n1.y);
        const delta = this.stiffness * (currentDist - this.dist) / currentDist;
        dx *= delta;
        dy *= delta;

        if (this.firstRun) {
            this.firstRun = false;

            if (!this.n1.fixed) {
                this.n1.x += dx;
                this.n1.y += dy;
            }

            if (!this.n0.fixed) {
                this.n0.x -= dx;
                this.n0.y -= dy;
            }

            return;
        }

        let m1 = this.n0.mass + this.n1.mass;
        let m2 = this.n0.mass / m1;
        m1 = this.n1.mass / m1;

        if (!this.n1.fixed) {
            this.n1.x += dx * m2;
            this.n1.y += dy * m2;
        }

        if (!this.n0.fixed) {
            this.n0.x -= dx * m1;
            this.n0.y -= dy * m1;
        }
    }
}
